import React, { useEffect, useState } from 'react';
import GeneralDialogLayout from '@Containers/Dialogs/GeneralDialogLayout';
import { ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import GlobalSpinner from '@Containers/GlobalSpinner/GlobalSpinner';
import PublishedParameters, { extractDefaultPublishedParameters } from '@Pages/RetroSynthesis/ProjectParametersDialog/PublishedParameters';
import { getCheckedValue, OptionSwitch } from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import ProjectParametersSection from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersSection';
import PredictedParametersPai, {
  extractDefaultPredictedParametersPai,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/PredictedParametersPai';
import { TooltipConstants } from '@Components/Tooltip';
// eslint-disable-next-line import/named
import { InfoTooltipOnClick } from '@Components/InfoTooltip';
import Auth from '@Services/Auth';
import {
  isPredictiveRetrosynthesisEnabled,
  isIktosPredictiveRetrosynthesisEnabled,
  isPaiPredictiveRetrosynthesisEnabled,
} from '@Pages/RetroSynthesis/predictiveUtils';
import PredictedParametersIktos, {
  extractDefaultPredictedParametersIktos,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/PredictedParametersIktos';
import { PREDICTED_PARAMS_SECTION_LABEL, PUBLISHED_PARAMS_SECTION_LABEL } from './constants';
import { SECTION_TYPE } from './anaytics';

import './ProjectParametersDialog.scss';

export const RX2_SHOW_AUTOPLAN_PREF = 'RX2_SHOW_AUTOPLAN_PREF';

function ProjectParametersDialog({
  rxn,
  databaseId,
  structure,
  response,
  vendorsInfo,
  onSubmit = () => null,
  onCancel = () => null,
  onSaveAutoplanOptions = () => null,
  isImport = false,
}) {
  const isPredictedIktos = isIktosPredictiveRetrosynthesisEnabled();
  const isPredictedPai = isPaiPredictiveRetrosynthesisEnabled() && !isPredictedIktos;
  const showPredictedParams = isPredictedPai || isPredictedIktos;
  const isAnonymousUser = Auth.isAnonymousUser();
  const isPredictiveRSEnabled = isPredictiveRetrosynthesisEnabled();
  const [publishedParams, setPublishedParams] = useState({});
  const [predictedParams, setPredictedParams] = useState({});
  const [publishedEnabled, setPublishedEnabled] = useState(true);
  const [publishedEditMode, setPublishedEditMode] = useState(false);
  const [predictedEnabled, setPredictedEnabled] = useState(isPredictiveRSEnabled);
  const [predictedEditMode, setPredictedEditMode] = useState(false);
  const [showAutoplanPref, setShowAutoplanPref] = useState(OptionSwitch.ON); // It is a string 'true' or 'false'
  const skipProjectCreation = !isImport && getCheckedValue(RX2_SHOW_AUTOPLAN_PREF, response) === OptionSwitch.OFF;

  const doSubmit = (defaultPublishedParams, defaultPredictedParams) => {
    if (!response) {
      Logger.warn('Attempt to save project without project parameters got prom user profile options');
      return;
    }
    if (showAutoplanPref === OptionSwitch.OFF) {
      onSaveAutoplanOptions({ preferences: [{ name: RX2_SHOW_AUTOPLAN_PREF, value: showAutoplanPref === OptionSwitch.ON }] });
    }
    onSubmit({
      rxn,
      ...(publishedEnabled && { publishedParams: defaultPublishedParams || publishedParams }),
      ...(predictedEnabled && { predictedParams: defaultPredictedParams || predictedParams }),
      databaseId,
      structure,
    });
  };

  useEffect(() => {
    if (!response) {
      return;
    }
    const defaultPublishedParams = { ...extractDefaultPublishedParameters(response) };
    let defaultPredictedParams;
    if (isPredictedPai) {
      defaultPredictedParams = { ...extractDefaultPredictedParametersPai(response) };
    } else if (isPredictedIktos) {
      defaultPredictedParams = { ...extractDefaultPredictedParametersIktos(response) };
    }

    if (skipProjectCreation) {
      doSubmit(defaultPublishedParams, defaultPredictedParams);
    } else {
      // init default params
      setPublishedParams(defaultPublishedParams);
      setPredictedParams(defaultPredictedParams);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  if (isEmpty(response) || skipProjectCreation) {
    return <GlobalSpinner isShown />;
  }

  const publishedParametersEditable = !showPredictedParams || publishedEditMode;
  const predictedParametersEditable = showPredictedParams && predictedEditMode;

  const tooltipId = TooltipConstants.ids.RetrosynthesisParamsDialog;
  return (
    <GeneralDialogLayout
      className={cn('popup-project-parameters', { double: showPredictedParams })}
      selfClose={() => onCancel()}
      header={
        <div className="popup-project-parameters__header">
          <div>
            <svg className="popup-project-parameters__header-icon">
              <use xlinkHref="#icon-cog" />
            </svg>
          </div>
          <h4>Parameters</h4>
        </div>
      }
      footer={
        <div className="popup-project-parameters__footer">
          <Checkbox
            className="footer-default-checkbox"
            label="Always show screen before creating plan"
            onChange={() => setShowAutoplanPref(showAutoplanPref === OptionSwitch.ON ? OptionSwitch.OFF : OptionSwitch.ON)}
            checked={showAutoplanPref === OptionSwitch.ON}
          />
          <ButtonWithIcon
            type="primary"
            sprite="ChevronRight"
            iconRight
            size="small"
            className="e2e-create-plans"
            onClick={() => doSubmit()}>
            Create Plans
          </ButtonWithIcon>
        </div>
      }>
      <div className="popup-project-parameters__params-container">
        {showPredictedParams && (
          <ProjectParametersSection
            className="predicted"
            label="Predicted"
            infoTooltipAriaLabel={PREDICTED_PARAMS_SECTION_LABEL}
            tooltip={
              isAnonymousUser ? (
                'Note: to use this feature please sign in first'
              ) : (
                <ul className="info-tooltip-content-list">
                  <li className="info-tooltip-content-list-item">
                    Here you can alter the predictive retrosynthesis parameters to tailor your results. Please click &quot;Edit&quot; to
                    modify them.
                  </li>
                  <li className="info-tooltip-content-list-item">(refer to Reaxys help system for more)</li>
                </ul>
              )
            }
            vendorsInfo={vendorsInfo}
            sectionType={isPredictedPai ? SECTION_TYPE.PAI : SECTION_TYPE.IKTOS}
            tooltipFor={tooltipId}
            editable={isPredictiveRSEnabled}
            selected={predictedEnabled}
            onSelectedChanged={(value) => {
              if (!value && !publishedEnabled) {
                setPublishedEnabled(true);
              }
              setPredictedEnabled(value);
            }}
            onEditSateChanged={setPredictedEditMode}
            editSate={predictedEditMode}
            disabled={isAnonymousUser}>
            {isPredictedPai && (
              <PredictedParametersPai
                onParamsChanged={(changedParams) => {
                  setPredictedParams({ ...predictedParams, ...changedParams });
                }}
                projectParams={predictedParams}
                editable={predictedParametersEditable}
                tooltipFor={tooltipId}
                optionResponse={response}
              />
            )}
            {isPredictedIktos && (
              <PredictedParametersIktos
                onParamsChanged={(changedParams) => {
                  setPredictedParams({ ...predictedParams, ...changedParams });
                }}
                projectParams={predictedParams}
                editable={predictedParametersEditable}
                tooltipFor={tooltipId}
                optionResponse={response}
              />
            )}
          </ProjectParametersSection>
        )}
        <ProjectParametersSection
          className="published"
          label="Published"
          infoTooltipAriaLabel={PUBLISHED_PARAMS_SECTION_LABEL}
          tooltip={
            <ul className="info-tooltip-content-list">
              <li className="info-tooltip-content-list-item">
                Here you can alter the published retrosynthesis parameters to tailor your results. Please click &quot;Edit&quot; to modify
                them.
              </li>
              <li className="info-tooltip-content-list-item">(refer to Reaxys help system for more)</li>
            </ul>
          }
          tooltipFor={tooltipId}
          editable={showPredictedParams}
          sectionType={SECTION_TYPE.PUBLISHED}
          selected={publishedEnabled}
          onSelectedChanged={(value) => {
            if (!value && isAnonymousUser) {
              return;
            }
            if (!value && !predictedEnabled) {
              setPredictedEnabled(true);
            }
            setPublishedEnabled(value);
          }}
          onEditSateChanged={setPublishedEditMode}
          editSate={publishedEditMode}>
          <PublishedParameters
            onParamsChanged={(changedParams) => {
              setPublishedParams({ ...publishedParams, ...changedParams });
            }}
            projectParams={publishedParams}
            editable={publishedParametersEditable}
            optionResponse={response}
            tooltipFor={tooltipId}
          />
        </ProjectParametersSection>
      </div>
      <InfoTooltipOnClick id={tooltipId} />
    </GeneralDialogLayout>
  );
}

ProjectParametersDialog.propTypes = {
  rxn: PropTypes.string,
  databaseId: PropTypes.string,
  structure: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onSaveAutoplanOptions: PropTypes.func,
  response: PropTypes.array,
  vendorsInfo: PropTypes.array,
  isImport: PropTypes.bool,
};

export default ProjectParametersDialog;
